import { Component, AfterViewInit, OnDestroy, ViewChild, ElementRef, Output, EventEmitter, NgZone, ChangeDetectorRef, Input, Inject, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import type { Loader } from '@googlemaps/js-api-loader';
import { getGoogleMapsLoader } from 'src/app/_helpers/google-maps-loader-helper';
import { ToastService } from 'src/app/services/toast.service';
import type { GmpPlaceAutocompleteElement, GmpSelectEvent } from 'src/app/models/google-places.model';
import { LocationPickerDialogData } from 'src/app/models/location-picker.model';

const SELECTED_POSITION_ZOOM = 8;
const DEFAULT_POSITION_ZOOM = 4;
const ADDRESS_ZOOM = 18;

@Component({
  standalone: false,
  selector: 'app-location-picker',
  templateUrl: './location-picker.component.html',
  styleUrls: ['./location-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None
})
export class LocationPickerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('gMap', { static: false }) mapElement!: ElementRef;
  @ViewChild('addressSearchContainer', { static: false }) addressSearchContainer?: ElementRef<HTMLDivElement>;
  @Input() initialPosition?: google.maps.LatLngLiteral;
  @Output() coordinatesSelected = new EventEmitter<google.maps.LatLngLiteral>();

  readonly clickOutsideExclude = '.geo-location-selector, gmp-place-autocomplete';

  private map!: google.maps.Map;
  private marker?: google.maps.Marker;
  private placeAutocompleteEl: GmpPlaceAutocompleteElement | null = null;
  private readonly defaultPosition: google.maps.LatLngLiteral = { lat: 39.8283, lng: -98.5795 };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LocationPickerDialogData,
    private dialogRef: MatDialogRef<LocationPickerComponent>,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private toastService: ToastService
  ) {
    this.initialPosition = data?.initialPosition;
  }

  ngAfterViewInit(): void {
    this.getMapsLoader().load().then(() => {
      this.ngZone.runOutsideAngular(() => {
        this.cdr.detectChanges();
        this.initMap();
        this.initAddressAutocomplete();
      });
    }).catch((error: unknown) => {
      this.ngZone.run(() => this.toastService.showError(error));
    });
  }

  protected getMapsLoader(): Loader {
    return getGoogleMapsLoader(this.translate.currentLang);
  }

  private initMap(): void {
    const isInitialValid = this.isValidPosition(this.initialPosition);
    const initialCenter = this.isValidPosition(this.data?.initialCenter) ? this.data.initialCenter : undefined;
    const position = isInitialValid ? this.initialPosition! : initialCenter ?? this.defaultPosition;
    if (!this.mapElement) return;
    this.map = new google.maps.Map(this.mapElement.nativeElement, {
      center: position,
      zoom: isInitialValid || initialCenter ? SELECTED_POSITION_ZOOM : DEFAULT_POSITION_ZOOM
    });

    if (isInitialValid) {
      this.marker = new google.maps.Marker({
        map: this.map,
        position: position,
        draggable: false
      });
    }

    this.map.addListener('click', (event: google.maps.MapMouseEvent) => {
      const lat = event.latLng?.lat();
      const lng = event.latLng?.lng();

      if (lat !== undefined && lng !== undefined) {
        const newPosition = { lat, lng };

        if (!this.marker) {
          this.marker = new google.maps.Marker({
            map: this.map,
            draggable: false
          });
        }

        this.marker.setPosition(newPosition);
        this.ngZone.run(() => this.coordinatesSelected.emit(newPosition));
      }
    });
  }

  private initAddressAutocomplete(): void {
    const container = this.addressSearchContainer?.nativeElement;
    if (!container) return;

    this.getMapsLoader().importLibrary('places').then((places) => {
      const lib = places as google.maps.PlacesLibrary & { PlaceAutocompleteElement: new () => GmpPlaceAutocompleteElement };
      const el = new lib.PlaceAutocompleteElement();
      container.appendChild(el);
      this.placeAutocompleteEl = el;

      el.addEventListener('gmp-select', (event: GmpSelectEvent) => {
        const prediction = event.placePrediction ?? event.detail?.placePrediction;
        if (!prediction) {
          return;
        }

        const place = prediction.toPlace();
        place.fetchFields({ fields: ['location'] }).then(() => {
          this.panToLocation(place.location);
        }).catch((error: unknown) => {
          this.ngZone.run(() => this.toastService.showError(error));
        });
      });
    }).catch((error: unknown) => {
      this.ngZone.run(() => this.toastService.showError(error));
    });
  }

  private panToLocation(location: google.maps.LatLng | null): void {
    if (!this.map || !location) return;
    this.map.panTo(location);
    this.map.setZoom(ADDRESS_ZOOM);
  }

  private isValidPosition(pos?: google.maps.LatLngLiteral): boolean {
    if (!pos) return false;
    const { lat, lng } = pos;
    const isLatValid = typeof lat === 'number' && lat >= -90 && lat <= 90;
    const isLngValid = typeof lng === 'number' && lng >= -180 && lng <= 180;
    const isNotZeroZero = !(lat === 0 && lng === 0);
    return isLatValid && isLngValid && isNotZeroZero;
  }

  closeLocationPicker(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.placeAutocompleteEl?.remove();
  }
}
